// Fase 17.3, Plano 06 (D-25) — lista de "Produto sem cadastro": itens de PV sem Material, do mais antigo para o mais novo.
// Usada na página "Cadastrar peça" (sem parâmetros) e no filtro de situação da lista de peças.
import { useCallback, useEffect, useState } from 'react';
import type { ItemSemCadastroDto } from '@pieltec/contracts';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { itensSemCadastro, linkCadastrarPecaDoItem, linkDoPedido, ROTULO_CADASTRAR_PECA, ROTULO_PRODUTO_SEM_CADASTRO } from '../../../lib/roteiros/cadastro-peca';

const CABECALHO = 'px-2 py-2 text-xs font-semibold uppercase tracking-wider';

/** Linhas da lista; "Cadastrar peça" só aparece para quem pode cadastrar (roteiros:gerenciar + pv:editar). */
export function TabelaProdutosSemCadastro({ itens, podeCadastrar }: { readonly itens: readonly ItemSemCadastroDto[]; readonly podeCadastrar: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <caption className="sr-only">Itens de pedido sem peça cadastrada</caption>
        <thead className="bg-muted text-left">
          <tr>
            {['Pedido', 'Cliente', 'Código do cliente', 'Descrição', 'Cor', 'Quantidade', 'Ação'].map((titulo) => (
              <th key={titulo} scope="col" className={CABECALHO}>{titulo}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {itens.map((item) => (
            <tr key={item.itemId} className="border-t">
              <td className="px-2 py-2"><a className="underline-offset-4 hover:underline" href={linkDoPedido(item.pedidoVendaId)}>{item.pvNumero}</a> · item {item.numeroItem}</td>
              <td className="px-2 py-2">{item.clienteNome}</td>
              <td className="px-2 py-2 font-semibold">{item.codigoItemCliente}</td>
              <td className="px-2 py-2">{item.descricao}</td>
              <td className="px-2 py-2">{item.cor ?? <span className="text-muted-foreground">—</span>}</td>
              <td className="px-2 py-2">{item.quantidade} {item.unidade}</td>
              <td className="px-2 py-2">
                {podeCadastrar ? (
                  <Button asChild size="sm" variant="outline"><a href={linkCadastrarPecaDoItem(item.pedidoVendaId, item.itemId)}>{ROTULO_CADASTRAR_PECA}</a></Button>
                ) : <span className="text-muted-foreground">Sem permissão</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Carrega e mostra os produtos sem cadastro, com estados de carregando, vazio e erro. */
export function ListaProdutosSemCadastro({ podeCadastrar, busca, clienteId }: { readonly podeCadastrar: boolean; readonly busca?: string; readonly clienteId?: string }) {
  const [itens, setItens] = useState<readonly ItemSemCadastroDto[] | null>(null);
  const [erro, setErro] = useState(false);

  const carregar = useCallback(async (sinal?: { vivo: boolean }) => {
    setErro(false);
    try {
      const lista = await itensSemCadastro(busca, clienteId);
      if (!sinal || sinal.vivo) setItens(lista);
    } catch {
      if (!sinal || sinal.vivo) setErro(true);
    }
  }, [busca, clienteId]);

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => { sinal.vivo = false; };
  }, [carregar]);

  if (erro) {
    return (
      <Card>
        <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
          <p className="text-sm text-destructive">Não foi possível carregar os produtos sem cadastro. Verifique a conexão e tente novamente.</p>
          <Button type="button" variant="outline" onClick={() => void carregar()}>Tentar novamente</Button>
        </CardContent>
      </Card>
    );
  }
  if (itens === null) {
    return <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando produtos sem cadastro">{Array.from({ length: 6 }).map((_, indice) => <Skeleton key={indice} className="h-8 w-full" />)}</div>;
  }
  if (itens.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
          <p className="text-sm font-semibold">Nenhum produto sem cadastro.</p>
          <p className="text-sm text-muted-foreground">Todos os itens de pedido já têm peça cadastrada.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex items-center gap-2 border-b px-4 py-2">
        <Badge variant="destructive">{ROTULO_PRODUTO_SEM_CADASTRO}</Badge>
        <span className="text-sm text-muted-foreground">{itens.length === 1 ? '1 item bloqueia a OP' : `${itens.length} itens bloqueiam a OP`}</span>
      </div>
      <TabelaProdutosSemCadastro itens={itens} podeCadastrar={podeCadastrar} />
    </Card>
  );
}
